/* eslint-disable react-hooks/rules-of-hooks */
import { Suspense, lazy } from 'react';
import { Navigate, useRoutes } from 'react-router';
// layouts
import LoadingScreen from 'src/minimals/LoadingScreen';
import { useSelector } from 'react-redux';
import { userSelectors } from 'src/selectors';
import AuthPageWrapper from 'src/pages/authentication/AuthPageWrapper';
import CustomerInvitation from 'src/pages/Customers/CustomerInvitation';
import RouteSolver from './RouteSolver';
import MainLayout from '../layouts/MainLayout';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from './guards/GuestGuard';
import AuthGuard from './guards/AuthGuard';
import AdminGuard from './guards/AdminGuard';
import WorkspaceGuard from './guards/WorkspaceGuard';
import WorkspaceInvitationGuard from './guards/WorkspaceInvitationGuard';
import WorkspacesPageGuard from './guards/WorkspacesPageGuard';
import RouteWildCardSolver from './RouteWildCardSolver';
import CustomerGuard from './guards/CustomerGuard';
import TreincoAppInvitation from 'src/pages/TreincoAppInvitation';
import AdminLayout from '../layouts/AdminLayout';
// components

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  const isAuth = useSelector(userSelectors.isAuth());

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isAuth && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed',
            }),
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      element: (
        <GuestGuard>
          <AuthPageWrapper />
        </GuestGuard>
      ),
      children: [
        { path: '', element: <Navigate to="/auth/login" replace /> },
        {
          path: 'login',
          element: <Login />,
        },
        {
          path: 'register',
          element: <Register />,
        },
        {
          path: 'forgot-password',
          element: <ForgotPassword />,
        },
      ],
    },
    {
      path: 'workspace/:workspaceId',
      element: (
        <AuthGuard to="dashboard">
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <RouteSolver /> },
        { path: 'dashboard', element: <Dashboard /> },
        {
          path: 'monitoring',
          element: <Monitoring />,
        },
        {
          path: 'traffic',
          element: <Traffic />,
        },
        { path: 'devices', element: <Devices fromCustomer={false} /> },
        {
          path: 'reports',
          children: [
            { path: '', element: <Reports /> },
            { path: 'devices-by-event', element: <DevicesByEvent /> },
            { path: 'devices-by-activity', element: <DevicesByActivity /> },
            { path: 'devices-by-last-event', element: <DevicesByLastEvent /> },
            { path: 'devices-without-image', element: <DevicesWithoutImage /> },
            {
              path: 'devices-by-last-position',
              element: <DevicesByLastPosition />,
            },
            { path: 'devices-without-place', element: <DevicesWithoutPlace /> },
          ],
        },
        {
          path: 'device/:deviceId',
          element: <DeviceProfilePage />,
        },
        {
          path: 'customers',
          element: <Customers />,
        },
        {
          path: 'customers/:customerId',
          element: <CustomerProfilePage />,
        },
        {
          path: 'places',
          element: <Places />,
        },
        {
          path: 'settings',
          element: <Settings />,
          children: [
            {
              path: '',
              element: (
                <RouteWildCardSolver redirection="/workspace/:workspaceId/settings/general" />
              ),
            },
            { path: 'general', element: <SettingsGeneral /> },
            { path: 'integrations', element: <SettingsIntegrations /> },
            { path: 'users', element: <SettingsUsers /> },
            { path: 'roles', element: <SettingsRoles /> },
            { path: 'eventGroups', element: <SettingsEventGroups /> },
            { path: 'events', element: <SettingsEvents /> },
            {
              path: 'notificationGroups',
              element: <SettingsNotificationGroups />,
            },
            {
              path: 'notificationContacts',
              element: <SettingsNotificationContacts />,
            },
            { path: 'models', element: <SettingsModels /> },
            { path: 'brands', element: <SettingsBrands /> },
            { path: 'resolutions', element: <SettingsResolutions /> },
            { path: 'actions', element: <SettingsActions /> },
            { path: 'items', element: <SettingsItems /> },
            { path: 'plans', element: <SettingsPlans /> },
            { path: 'invoice-series', element: <SettingsInvoiceSeries /> },
            {
              path: 'emergencyContacts',
              element: <SettingsEmergencyContacts />,
            },
            {
              path: 'emergencyContactLists',
              element: <SettingsEmergencyContactLists />,
            },
            {
              path: 'apiKeys',
              element: <SettingsApiKeys />,
            },
          ],
        },
        {
          path: 'emergencyContactList/:emergencyContactListId',
          element: <EmergencyContactList />,
        },
        {
          path: 'actions/:actionGroupId',
          element: <Actions />,
        },
        {
          path: 'invoices',
          element: <Invoices />,
        },
        {
          path: 'invoices/:invoiceId',
          element: <InvoiceProfilePage />,
        },
        {
          path: 'service-orders',
          element: <ServiceOrders />,
        },
      ],
    },
    {
      path: 'customer',
      element: (
        <CustomerGuard>
          <MainLayout to="dashboard" />
        </CustomerGuard>
      ),
      children: [
        { path: '', element: <CustomerDevices /> },
        {
          path: 'device/:deviceId',
          element: <DeviceProfilePage from="customer" />,
        },
      ],
    },
    // {
    //   path: 'customer',
    //   element: <MainLayout />,
    // },
    // Admin Mode
    {
      path: 'admin',
      element: (
        <AuthGuard to="admin">
          <AdminGuard>
            <AdminLayout />
          </AdminGuard>
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Navigate to="/admin/workspaces" replace /> },
        { path: 'workspaces', element: <AdminWorkspaces /> },
        {
          path: 'workspaces/:workspaceId',
          element: <AdminWorkspaceProfilePage />,
        },
        { path: 'users', element: <AdminUsers /> },
        { path: 'integrations', element: <AdminIntegrations /> },
        // A stale admin URL (e.g. the removed connector-versions page) would
        // otherwise match the app's bare '*' route and show a blank page.
        { path: '*', element: <Navigate to="/admin/workspaces" replace /> },
      ],
    },

    // este user es el que esta logueado
    {
      path: 'profile',
      element: (
        <AuthGuard to="dashboard">
          <MainLayout>
            <Profile />
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: 'create-workspace',
      element: (
        <WorkspaceGuard>
          <CreateWorkspace />
        </WorkspaceGuard>
      ),
    },
    {
      path: 'workspaces',
      element: (
        <WorkspacesPageGuard>
          <Workspaces />
        </WorkspacesPageGuard>
      ),
    },
    {
      path: 'Workspace-invitation',
      element: (
        <WorkspaceInvitationGuard>
          <WorkspaceInvitation />
        </WorkspaceInvitationGuard>
      ),
    },
    {
      path: 'customer-invitation',
      // element: <RouteSolver />,
      children: [
        {
          path: 'accept',
          element: (
            <WorkspaceInvitationGuard>
              <CustomerInvitation action="accept" />
            </WorkspaceInvitationGuard>
          ),
        },
        {
          path: 'reject',
          element: <CustomerInvitation action="reject" />,
        },
      ],
    },
    {
      path: 'treinco-app',
      children: [
        {
          path: 'accept',
          element: <TreincoAppInvitation action="accept" />,
        },
        {
          path: 'reject',
          element: <TreincoAppInvitation action="reject" />,
        },
      ],
    },
    {
      path: '/',
      element: <RouteSolver />,
      children: [
        { path: 'login', element: <Login /> },
        { path: '/', element: <Navigate to="/auth/login" /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'validate-account', element: <ValidateAccount /> },
        { path: 'logout', element: <Logout /> },
        { path: 'privacy-policy', element: <PrivacyPolicy /> },
        // { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    // { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('src/pages/authentication/Login')));
const Register = Loadable(
  lazy(() => import('src/pages/authentication/Register'))
);
const ForgotPassword = Loadable(
  lazy(() => import('src/pages/authentication/ForgotPassword'))
);

// afuera de authentication
const ResetPassword = Loadable(
  lazy(() => import('src/pages/authentication/ResetPassword'))
);
const ValidateAccount = Loadable(
  lazy(() => import('src/pages/authentication/ValidateAccount'))
);
const Logout = Loadable(lazy(() => import('src/pages/authentication/Logout')));
const WorkspaceInvitation = Loadable(
  lazy(() => import('src/pages/workspace/Invitation'))
);
// dashboard
const Dashboard = Loadable(lazy(() => import('src/pages/Dashboard')));

// workspace
const CreateWorkspace = Loadable(
  lazy(() => import('src/pages/workspace/Create'))
);
const Workspaces = Loadable(
  lazy(() => import('src/pages/workspace/Workspaces'))
);
const Settings = Loadable(lazy(() => import('src/pages/workspace/Settings')));

// settings components
const SettingsGeneral = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/SettingsGeneral'))
);
const SettingsIntegrations = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Integrations'))
);
const SettingsUsers = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Users'))
);
const SettingsRoles = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Roles'))
);

const SettingsEventGroups = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/EventGroups'))
);
const SettingsEvents = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Events'))
);
const SettingsNotificationGroups = Loadable(
  lazy(
    () => import('src/pages/workspace/Settings/components/NotificationGroups')
  )
);
const SettingsNotificationContacts = Loadable(
  lazy(
    () => import('src/pages/workspace/Settings/components/NotificationContacts')
  )
);
const SettingsModels = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Models'))
);
const SettingsBrands = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Brands'))
);
const SettingsResolutions = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Resolutions'))
);
const SettingsEmergencyContacts = Loadable(
  lazy(
    () => import('src/pages/workspace/Settings/components/EmergencyContacts')
  )
);
const SettingsEmergencyContactLists = Loadable(
  lazy(
    () =>
      import('src/pages/workspace/Settings/components/EmergencyContactLists')
  )
);

const SettingsApiKeys = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/ApiKeys'))
);

const SettingsActions = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Actions'))
);
const SettingsItems = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Items'))
);
const SettingsPlans = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/Plans'))
);
const SettingsInvoiceSeries = Loadable(
  lazy(() => import('src/pages/workspace/Settings/components/InvoiceSeries'))
);

// user
const Profile = Loadable(lazy(() => import('src/pages/Profile')));

// emergency contact list (single)
const EmergencyContactList = Loadable(
  lazy(() => import('src/pages/EmergencyContactList'))
);

// actions
const Actions = Loadable(lazy(() => import('src/pages/Actions')));

//service orders
const ServiceOrders = Loadable(lazy(() => import('src/pages/ServiceOrders')));
const Invoices = Loadable(lazy(() => import('src/pages/Invoices')));
const InvoiceProfilePage = Loadable(
  lazy(() => import('src/pages/Invoices/InvoiceProfile'))
);
// // users

// device
const Devices = Loadable(lazy(() => import('src/pages/Devices')));

// reports
const Reports = Loadable(lazy(() => import('src/pages/Reports')));
const DevicesByEvent = Loadable(
  lazy(() => import('src/pages/Reports/DevicesByEvent'))
);
const DevicesByLastEvent = Loadable(
  lazy(() => import('src/pages/Reports/DevicesByLastEvent'))
);
const DevicesByActivity = Loadable(
  lazy(() => import('src/pages/Reports/DevicesByActivity'))
);
const DevicesByLastPosition = Loadable(
  lazy(() => import('src/pages/Reports/DevicesByLastPosition'))
);
const DevicesWithoutImage = Loadable(
  lazy(() => import('src/pages/Reports/DevicesWithoutImage'))
);
const DevicesWithoutPlace = Loadable(
  lazy(() => import('src/pages/Reports/DevicesWithoutPlace'))
);
const DeviceProfilePage = Loadable(
  lazy(() => import('src/pages/DeviceProfile/DeviceProfilePage'))
);
// device (As Customer)
const CustomerDevices = Loadable(
  lazy(() => import('src/pages/Devices/CustomerDevices'))
);

// customers
const Customers = Loadable(lazy(() => import('src/pages/Customers')));
const CustomerProfilePage = Loadable(
  lazy(() => import('src/pages/Customers/CustomerProfile'))
);

// places
const Places = Loadable(lazy(() => import('src/pages/Places')));

// monitoreo
const Monitoring = Loadable(lazy(() => import('src/pages/Monitoring')));
const Traffic = Loadable(lazy(() => import('src/pages/Traffic')));

// error pages
const Page500 = Loadable(lazy(() => import('src/pages/Page500')));
const NotFound = Loadable(lazy(() => import('src/pages/Page404')));

// privacy policy
const PrivacyPolicy = Loadable(lazy(() => import('src/pages/PrivacyPolicy')));

// admin
const AdminWorkspaces = Loadable(
  lazy(() => import('src/pages/Admin/Workspaces'))
);
const AdminWorkspaceProfilePage = Loadable(
  lazy(() => import('src/pages/Admin/WorkspaceProfile/WorkspaceProfilePage'))
);
const AdminUsers = Loadable(lazy(() => import('src/pages/Admin/Users')));
const AdminIntegrations = Loadable(
  lazy(() => import('src/pages/Admin/Integrations'))
);
